import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, Table, Tag, Button, Upload, Checkbox, Input, Modal, Space, Spin, Empty, Tooltip } from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import hiringDocumentPackageService from "Services/HiringDocumentPackageService";
import API from "api/endpoints";
import { downloadFile } from "Common/downloadFile";
import { JobApplicationStatusEnum } from "Constants/Enums/JobApplicantStatusEnum";
import {
  NextAction,
  PackageStatusLabel,
  PackageStatusColor,
  ItemStatusLabel,
  ItemStatusColor,
} from "Constants/Enums/HiringDocumentPackageEnum";

const { TextArea } = Input;

// HR-side view of the dynamic hiring document package (per Mudassir's Jun-16 handoff).
// Everything is driven off the package GET response, never hardcoded.
// External-only is enforced by the parent tab list.
function DocumentsTab({ jobApplicationId, applicantData, isPostingFilled = false }) {
  const navigate = useNavigate();
  const status = applicantData?.status;
  const isReject = status === JobApplicationStatusEnum.Reject;

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // HR template files keyed by document item id: { [itemId]: File }
  const [templateFiles, setTemplateFiles] = useState({});

  // HR verify modal state
  const [verifyItem, setVerifyItem] = useState(null);
  const [inPersonVerified, setInPersonVerified] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState("");
  const [verifyFile, setVerifyFile] = useState(null);

  // Per-button loading for authenticated file downloads (Jun-17 rule: download
  // through the backend API, not direct static paths).
  const [downloadingKey, setDownloadingKey] = useState(null);

  const loadPackage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await hiringDocumentPackageService.getByApplicant(jobApplicationId);
      setPkg(data);
    } catch (e) {
      // 404 = no package yet (expected before HR initiates).
      if (e.response?.status === 404) {
        setPkg(null);
      } else {
        console.error("Error loading document package:", e);
        toast.error("Failed to load document package");
        setPkg(null);
      }
    } finally {
      setLoading(false);
    }
  }, [jobApplicationId]);

  useEffect(() => {
    if (jobApplicationId) loadPackage();
  }, [jobApplicationId, loadPackage]);

  const handleInitiate = async () => {
    setBusy(true);
    try {
      await hiringDocumentPackageService.startPackage(jobApplicationId);
      toast.success("Document package initiated");
      await loadPackage();
    } catch (e) {
      console.error("Error initiating package:", e);
      toast.error("Failed to initiate document package");
    } finally {
      setBusy(false);
    }
  };

  const documents = pkg?.documents || [];
  const templateItems = documents.filter((d) => d.nextAction === NextAction.HrUploadTemplate);

  const handleSubmitTemplates = async () => {
    const ids = templateItems.map((d) => d.hiringDocumentItemId);
    if (ids.some((id) => !templateFiles[id])) {
      toast.warning("Please attach a file for every document before submitting");
      return;
    }
    setBusy(true);
    try {
      const files = ids.map((id) => templateFiles[id]);
      await hiringDocumentPackageService.submitTemplates(pkg.hiringDocumentPackageId, ids, files);
      toast.success("Templates submitted to applicant");
      setTemplateFiles({});
      await loadPackage();
    } catch (e) {
      console.error("Error submitting templates:", e);
      toast.error("Failed to submit templates");
    } finally {
      setBusy(false);
    }
  };

  const handleWaive = async (itemId) => {
    setBusy(true);
    try {
      await hiringDocumentPackageService.waiveDocument(itemId);
      toast.success("Document waived");
      await loadPackage();
    } catch (e) {
      console.error("Error waiving document:", e);
      toast.error("Failed to waive document");
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    setBusy(true);
    try {
      await hiringDocumentPackageService.verifyDocument(
        verifyItem.hiringDocumentItemId,
        inPersonVerified,
        verifyNotes,
        verifyFile
      );
      toast.success("Document verified");
      setVerifyItem(null);
      await loadPackage();
    } catch (e) {
      console.error("Error verifying document:", e);
      toast.error("Failed to verify document");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadFile = async (itemId, segment, path) => {
    const key = `${itemId}-${segment}`;
    setDownloadingKey(key);
    try {
      const url = API.hiringDocumentPackage.fileDownload(itemId, segment);
      const fileName = (path || "").split("/").pop() || "document";
      await downloadFile(url, fileName);
    } catch (e) {
      console.error("Error downloading file:", e);
      toast.error("Failed to download the file");
    } finally {
      setDownloadingKey(null);
    }
  };

  // ---------- Render guards ----------
  if (isReject) {
    return <Empty description="Candidate rejected — no further actions are available." />;
  }
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  // No package yet
  if (!pkg) {
    if (status === JobApplicationStatusEnum.FeedbackAdded && !isPostingFilled) {
      return (
        <div style={{ padding: 24 }}>
          <Card title="Hiring Documents">
            <p style={{ marginBottom: 16 }}>
              Feedback is recorded for this candidate. Start the document package to generate the
              required documents based on their employment type and work authorization.
            </p>
            <Button type="primary" loading={busy} onClick={handleInitiate}>
              Initiate Documents
            </Button>
          </Card>
        </div>
      );
    }
    if (status === JobApplicationStatusEnum.FeedbackAdded && isPostingFilled) {
      return <Empty description="This posting is marked Filled — starting documents is disabled." />;
    }
    return <Empty description="Documents become available once interview feedback is recorded." />;
  }

  // ---------- Package exists ----------
  const columns = [
    {
      title: "Document",
      dataIndex: "displayName",
      key: "doc",
      render: (v, r) => v || r.documentType || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={ItemStatusColor[v] || "default"}>{ItemStatusLabel[v] || v}</Tag>,
    },
    {
      title: "File",
      key: "file",
      render: (_, r) => {
        // A document can have several file versions (per Mudassir's Jun-16/17
        // notes): HR template/source, applicant returned, and HR final verified.
        // Show every version that exists rather than a single generic link.
        const versions = [
          {
            label: "HR Template",
            tip: "HR template or source document",
            segment: "HrTemplate",
            path: r.hrTemplateFilePath,
          },
          {
            label: "Applicant File",
            tip: "Applicant submitted or returned document",
            segment: "ApplicantSubmitted",
            path: r.applicantUploadedFilePath,
          },
          {
            label: "Final Copy",
            tip: "HR final verified document",
            segment: "HrFinal",
            path: r.hrFinalFilePath,
          },
        ].filter((v) => v.path);
        if (versions.length === 0) return "-";
        return (
          <Space size="small" wrap>
            {versions.map((v) => {
              const key = `${r.hiringDocumentItemId}-${v.segment}`;
              return (
                <Tooltip key={v.segment} title={v.tip}>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadingKey === key}
                    onClick={() => handleDownloadFile(r.hiringDocumentItemId, v.segment, v.path)}
                  >
                    {v.label}
                  </Button>
                </Tooltip>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => {
        if (r.nextAction === NextAction.HrVerify) {
          return (
            <Button
              size="small"
              onClick={() => {
                setVerifyItem(r);
                setInPersonVerified(false);
                setVerifyNotes("");
                setVerifyFile(null);
              }}
            >
              Verify
            </Button>
          );
        }
        if (r.nextAction === NextAction.Completed || r.nextAction === NextAction.Waived) {
          return null;
        }
        // Pending HR upload or pending applicant — HR can waive a required item.
        return (
          <Button size="small" danger loading={busy} onClick={() => handleWaive(r.hiringDocumentItemId)}>
            Waive
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Tag color={PackageStatusColor[pkg.status] || "default"} style={{ fontSize: 14, padding: "4px 12px" }}>
          Package: {PackageStatusLabel[pkg.status] || pkg.status}
        </Tag>
      </div>

      {/* HR one-submit template form (shown while there are HR-upload items) */}
      {templateItems.length > 0 && (
        <Card title="Submit HR Documents" style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 12 }}>
            Attach the source/template file for each document, then submit them all at once. HR Policy
            is system-provided and is not uploaded here.
          </p>
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            {templateItems.map((item) => (
              <div
                key={item.hiringDocumentItemId}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
              >
                <span>{item.displayName || item.documentType}</span>
                <Upload
                  maxCount={1}
                  beforeUpload={(file) => {
                    setTemplateFiles((prev) => ({ ...prev, [item.hiringDocumentItemId]: file }));
                    return false;
                  }}
                  onRemove={() =>
                    setTemplateFiles((prev) => {
                      const next = { ...prev };
                      delete next[item.hiringDocumentItemId];
                      return next;
                    })
                  }
                >
                  <Button icon={<UploadOutlined />} size="small">
                    Attach
                  </Button>
                </Upload>
              </div>
            ))}
          </Space>
          <Button type="primary" loading={busy} onClick={handleSubmitTemplates} style={{ marginTop: 12 }}>
            Submit Templates
          </Button>
        </Card>
      )}

      <Card title="Documents">
        <Table
          className="custom-table"
          rowKey={(r) => r.hiringDocumentItemId}
          columns={columns}
          dataSource={documents}
          pagination={false}
          size="small"
          locale={{ emptyText: "No documents in this package" }}
        />
      </Card>

      {pkg.canStartOnboarding && (
        <Card style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 12 }}>All required documents are complete. You can start onboarding.</p>
          <Button type="primary" onClick={() => navigate("/onboarding")}>
            Start Onboarding
          </Button>
        </Card>
      )}

      <Modal
        title={`Verify: ${verifyItem?.displayName || verifyItem?.documentType || ""}`}
        open={!!verifyItem}
        onCancel={() => setVerifyItem(null)}
        onOk={handleVerify}
        confirmLoading={busy}
        okText="Verify"
        destroyOnClose
      >
        <Checkbox checked={inPersonVerified} onChange={(e) => setInPersonVerified(e.target.checked)}>
          Original documents verified in person
        </Checkbox>
        <div style={{ marginTop: 12 }}>
          <TextArea
            rows={3}
            placeholder="Verification notes"
            value={verifyNotes}
            onChange={(e) => setVerifyNotes(e.target.value)}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <Upload
            maxCount={1}
            beforeUpload={(file) => {
              setVerifyFile(file);
              return false;
            }}
            onRemove={() => setVerifyFile(null)}
          >
            <Button icon={<UploadOutlined />}>Attach final signed scan</Button>
          </Upload>
        </div>
      </Modal>
    </div>
  );
}

DocumentsTab.propTypes = {
  jobApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  applicantData: PropTypes.object,
  isPostingFilled: PropTypes.bool,
};

export default DocumentsTab;
